import { List } from 'immutable'
import * as React from 'react'
import {
  BranchLocalizationLayout,
  LocalizationProps
} from '../../../Shared/components/Jumbo/BranchLocalization/BranchLocalizationLayout'
import { OpeningHours } from '../../../Shared/components/Jumbo/OpeningHours/OpeningHoursLayout'
import { getDrupalCurrentOpeningHours } from "../../../Shared/components/Jumbo/OpeningHours/OpeningHoursState"
import { ParsedParagraphOpeningHours } from '../../../Shared/drupal_models/types'
import { hyphen } from '../../../Shared/utils/hyphenate'
import { library } from '../../libraries'
import { LinkButton } from './library/button'
import { trackJumboButton } from './library/tracking'

interface HomeJumboProps {
  readonly kind: 'home-location' | 'home'
  readonly localizationProps?: LocalizationProps
}

export type JumboProps = {
  readonly title: string
  readonly contents: string | JSX.Element
  readonly imageUrlSmall?: string
  readonly imageUrl: string
  readonly imageUrlLarge: string
  readonly imageAlt: string
  readonly buttonUrl?: string
  readonly buttonTitle?: any
  readonly size: 'small' | 'large'
  readonly openingHours?: List<ParsedParagraphOpeningHours>
} & (
    | { readonly kind?: 'default' | 'activity' | 'collection' }
    | HomeJumboProps)

export const isHomeJumbo = (props: JumboProps): props is (JumboProps & HomeJumboProps) =>
  (props.kind =='home' || props.kind == 'home-location') && !!props.localizationProps

interface JumboState {}

export const jumboStateZero: JumboState = {}

export const renderContents = (contents: string | JSX.Element, htmlElement: JSX.Element, jsxElement: JSX.Element) => {
  return typeof contents == 'string' ? (
    <htmlElement.type {...htmlElement.props} key={htmlElement.key} dangerouslySetInnerHTML={{ __html: contents }} />
  ) : (
    <jsxElement.type key={jsxElement.key} {...jsxElement.props}>
      {contents}
    </jsxElement.type>
  )
}

export const Jumbo = (props: JumboProps) => {
  const openingHours = props.openingHours ? <OpeningHours {...getDrupalCurrentOpeningHours(props.openingHours)} /> : <></>

  return props.size == 'large' ?
    (props.kind === 'home')
      ? library().homeJumbo(props)
      : (
        // Default 'large': has title, intro, button and image (55-45 columns) (+ filter bar on Activity/Collection page)
        <header
          className={`jumbotron jumbotron--large jumbotron--default${
            props.kind === 'activity' || props.kind === 'collection' ? ' jumbotron--activity' : ''
            }`}
            >
          <div className="jumbotron__outer">
            <div className="jumbotron__inner container--full gutter--m align--center">
              <div className="jumbotron__content container--s">
                <h1 className="jumbotron__title title--xl">{hyphen(props.title)}</h1>

                {props.contents.toString().length > 0 || (props.buttonUrl && props.buttonTitle) ? (
                  <div className="jumbotron__intro">
                    <div className="text--styled">
                      {props.contents.toString().length > 0 && renderContents(props.contents, <div />, <div />)}
                    </div>

                    {openingHours}

                    {props.buttonUrl && props.buttonTitle && (
                      <LinkButton
                        variant="white"
                        arrow={true}
                        route={props.buttonUrl}
                        handle={() => trackJumboButton(props.buttonTitle)}
                        className="button button--primary button--arrow jumbotron__clickable"
                      >
                        <span className="button__label">{props.buttonTitle}</span>
                      </LinkButton>
                    )}
                  </div>
                ) : <></>}
              </div>

              {props.imageUrl && (
                <div className="jumbotron__visual fit-object" aria-hidden="true">
                  <img
                    src={props.imageUrlSmall}
                    srcSet={`${props.imageUrlSmall} 470w, ${props.imageUrl} 940w, ${props.imageUrlLarge} 1880w`}
                    width="940"
                    height="940"
                    alt={props.imageAlt}
                  />
                </div>
              )}
            </div>
          </div>
        </header>
      ) : (
      // Default 'small': has title and intro
      <header className="jumbotron jumbotron--small">
        <div className="jumbotron__outer">
        {isHomeJumbo(props) && <BranchLocalizationLayout {...props.localizationProps} />}
          <div className="jumbotron__inner container--mg align--center">
            <div className="jumbotron__content container--s">
              <h1 className="jumbotron__title title--xl">{hyphen(props.title)}</h1>

              {props.contents.toString().length > 0 && (
                <div className="jumbotron__intro text--styled container--s">
                  {renderContents(props.contents, <p />, <div />)}
                </div>
              )}
              {openingHours}
          </div>

          {/* TODO: copied this over from the large version: check if 'if-statement' is correct and need different crop */}
          {props.imageUrl && (
            <div className="jumbotron__visual fit-object">
              <img
                src={props.imageUrl}
                srcSet={`${props.imageUrl} 440w, ${props.imageUrlLarge} 880w`}
                width="440"
                height="440"
                alt={props.imageAlt}
              />
            </div>
          )}
        </div>
      </div>
      </header>
      )
}

{
  /* TH: Added to styleguide! */
}
